import axios from "axios";
import { getCookie } from "tiny-cookie";
import { endpoints } from "@/endpoints";
import { config } from "@/config/envConfig";
import { useGtm } from "@gtm-support/vue-gtm";
import type { RegisterForm } from "@/api/companies";
import type {
  DowngradeForm,
  CancelPlanForm,
  CancelAddonForm,
  SubmitUserForm,
  SubmitCompanyForm,
  RequestAddons,
  RequestLocations,
  RequestResources,
} from "./types";

export * from "./types";

const formatFields = <T>(payload: T) =>
  (Object.keys(payload as object) as Array<keyof T>).map((key) => {
    const value = payload[key];
    return {
      name: key,
      value: Array.isArray(value) ? value.join(";") : value,
    };
  });

const submitForm = <T>(payload: T, formId?: string) => {
  if (!config.HUBSPOT_PORTAL_ID) {
    throw new Error("No hubspot portal id set");
  }
  if (!formId) {
    throw new Error("No hubspot form GUID set");
  }

  return axios.post(
    endpoints.hubspot.form(config.HUBSPOT_PORTAL_ID, formId),
    { fields: formatFields(payload) },
    { withCredentials: false }
  );
};

const submitRegisterForm = <T>(payload: T, formId?: string) => {
  if (!config.HUBSPOT_PORTAL_ID) {
    throw new Error("No hubspot portal id set");
  }
  if (!formId) {
    throw new Error("No hubspot form GUID set");
  }

  return axios.post(
    endpoints.hubspot.form(config.HUBSPOT_PORTAL_ID, formId),
    {
      fields: formatFields(payload),
      context: {
        hutk: getCookie("hubspotutk"),
        pageUri: window.location.href,
        pageName: "Secure Register Page",
      },
    },
    { withCredentials: false }
  );
};

const downgradePlan = (payload: DowngradeForm) => {
  return submitForm(payload, config.HUBSPOT_FORM_DOWNGRADE);
};

const cancelPlan = (payload: CancelPlanForm) => {
  return submitForm(payload, config.HUBSPOT_FORM_CANCEL_PLAN);
};

const cancelAddon = (payload: CancelAddonForm) => {
  return submitForm(payload, config.HUBSPOT_FORM_CANCEL_ADDON);
};

const requestLocations = (payload: RequestLocations) => {
  return submitForm(payload, config.HUBSPOT_FORM_REQUEST_LOCATIONS);
};

const requestAddons = (payload: RequestAddons) => {
  return submitForm(payload, config.HUBSPOT_FORM_REQUEST_ADDONS);
};

const requestResources = (payload: RequestResources) => {
  return submitForm(payload, config.HUBSPOT_FORM_REQUEST_RESOURCES);
};

const loadTrackingScripts = () => {
  if (config.HUBSPOT_WEBSITE_ID) {
    const hbtrack = document.createElement("script");
    hbtrack.src = `https://js.hs-scripts.com/${config.HUBSPOT_WEBSITE_ID}.js`;
    hbtrack.type = "text/javascript";
    hbtrack.async = true;

    document.body.appendChild(hbtrack);
  }

  /*
   * This code comes directly from the partnerstack website.
   * We've included this in the register module so it doesn't load on every page.
   * https://docs.partnerstack.com/docs/step-1-install-partnerstackjs
   * https://linear.app/swipedon/issue/MAGIC-9/update-the-new-register-page-with-partnerstack-code
   */
  if (config.PARTNERSTACK) {
    // eslint-disable-next-line
    const gs = document.createElement("script") as any;
    gs.src = "https://get.swipedon.com/pr/js";
    gs.type = "text/javascript";
    gs.async = true;
    gs.onload = gs.onreadystatechange = function loadGrowSumo() {
      const rs = this.readyState;
      if (rs && rs !== "complete" && rs !== "loaded") return;

      try {
        window.growsumo._initialize(config.PARTNERSTACK);
        if (typeof window.growsumoInit === "function") {
          window.growsumoInit();
        }
        // eslint-disable-next-line no-empty
      } catch (e) {}
    };

    const s = document.getElementsByTagName("script")[0];
    s.parentNode?.insertBefore(gs, s);
  }
};

const submitUser = async (payload: SubmitUserForm) => {
  await submitRegisterForm(payload, config.HUBSPOT_FORM_GUID);
  const gtm = useGtm();
  gtm?.trackEvent({ event: "register-form-step-1-success" });
  if (config.HUBSPOT_WEBSITE_ID && typeof window._hsq !== "undefined") {
    window._hsq.push([
      "trackCustomBehavioralEvent",
      {
        name: `pe${config.HUBSPOT_WEBSITE_ID}_register_free_trial___form_submission___step_1`,
      },
    ]);
  }
};

const submitCompany = async (payload: RegisterForm) => {
  const companyPayload: SubmitCompanyForm = {
    company: payload.company,
    address: payload.address,
    data_region: payload.dataRegion ?? null,
    terms: payload.terms,
    email: payload.email,
    how_did_you_hear: payload.howHeard,
  };
  await submitRegisterForm(companyPayload, config.HUBSPOT_FORM_GUID);
  const gtm = useGtm();
  gtm?.trackEvent({
    event: "register-form-step-2-success",
  });
  gtm?.trackEvent({
    event: "form_submit_enhance",
    enhanced_conversion_data: {
      email: payload.email,
    },
  });

  if (config.HUBSPOT_WEBSITE_ID && typeof window._hsq !== "undefined") {
    window._hsq.push([
      "trackCustomBehavioralEvent",
      {
        name: `pe${config.HUBSPOT_WEBSITE_ID}_register_free_trial___form_submission___step_2`,
      },
    ]);
  }
};

export const useHubspot = () => {
  return {
    downgradePlan,
    cancelPlan,
    cancelAddon,
    submitUser,
    submitCompany,
    loadTrackingScripts,
    requestLocations,
    requestAddons,
    requestResources,
  };
};
